if (!customElements.get('tabbed-content')) {
  function getScrollOffset(target) {
    const headerOffset = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-end-padded')
    ) || 0;
    return headerOffset - (target && target.id === 'all-reviews' ? 72 : 28);
  }

  function findReviewScrollTarget(selector) {
    const requested = selector ? document.querySelector(selector) : null;
    return document.getElementById('judgeme_product_reviews') ||
      document.querySelector('.jdgm-review-widget') ||
      (requested && requested.nextElementSibling && requested.nextElementSibling.querySelector
        ? requested.nextElementSibling.querySelector('.jdgm-review-widget')
        : null) ||
      requested;
  }

  function smoothScrollToTarget(selector) {
    const target = findReviewScrollTarget(selector);
    if (!target) return false;

    const top = target.getBoundingClientRect().top + window.pageYOffset - getScrollOffset(target);
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    return true;
  }

  class Tabs extends HTMLElement {
    constructor() {
      super();
      this.tabList = this.querySelector('[role="tablist"]');
      this.activeTab = this.tabList.querySelector('[aria-selected="true"]');
      this.isVerticalTablist = this.tabList.getAttribute('aria-orientation') === 'vertical';
      this.tabs = this.querySelectorAll('[role="tab"]');
      this.panels = this.querySelectorAll('[role="tabpanel"]');

      // If no tab is active by default, activate the first tab.
      if (!this.activeTab) {
        this.activeTab = this.tabs[0];
        this.activateTab(this.activeTab);
      }

      this.addListeners();
    }

    addListeners() {
      this.tabList.addEventListener('click', this.handleClick.bind(this));
      this.tabList.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    /**
     * Handles 'click' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const tab = evt.target.closest('[role="tab"]');
      if (!tab) return;

      if (this.scrollToTarget(tab)) {
        evt.preventDefault();
        return;
      }

      if (tab === this.activeTab) return;
      this.activateTab(tab);
    }

    /**
     * Handles 'keydown' events on the tablist.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      switch (evt.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
          evt.preventDefault();
          if (!this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();
          if (this.isVerticalTablist) {
            this.switchTabOnKeyPress(evt.key);
          }
          break;

        // Activate the first tab.
        case 'Home':
          evt.preventDefault();
          this.activateTab(this.tabs[0]);
          break;

        // Activate the last tab.
        case 'End':
          evt.preventDefault();
          this.activateTab(this.tabs[this.tabs.length - 1]);
          break;
      }
    }

    /**
     * Activates the next or previous tab according to the key pressed.
     * @param {string} key - Key pressed.
     */
    switchTabOnKeyPress(key) {
      if (key === 'ArrowRight' || key === 'ArrowDown') {
        // If active tab is the last tab.
        if (this.activeTab === this.tabs[this.tabs.length - 1]) {
          // Activate first tab.
          this.activateTab(this.tabs[0]);
        } else {
          // Activate next tab.
          this.activateTab(this.activeTab.nextElementSibling);
        }
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        // If active tab is the first tab.
        if (this.activeTab === this.tabs[0]) {
          this.activateTab(this.tabs[this.tabs.length - 1]);
        } else {
          // Activate the previous tab.
          this.activateTab(this.activeTab.previousElementSibling);
        }
      }
    }

    /**
     * Activates a tab.
     * @param {Element} tab - Tab element (button).
     */
    activateTab(tab) {
      if (this.scrollToTarget(tab)) return;

      this.deactivateActiveTab();

      Tabs.setTabState(tab, true);
      tab.removeAttribute('tabindex');
      this.activeTab = tab;

      if (document.activeElement.matches('.tablist__tab')) {
        tab.focus();
      }
    }

    /**
     * Scrolls linked pseudo-tabs to an external section instead of activating a panel.
     * @param {Element} tab - Tab element.
     * @returns {boolean} Whether a scroll target was handled.
     */
    scrollToTarget(tab) {
      const selector = tab && tab.getAttribute('data-scroll-target');
      if (!selector) return false;
      return smoothScrollToTarget(selector);
    }

    /**
     * Deactivates the currently active tab.
     */
    deactivateActiveTab() {
      Tabs.setTabState(this.activeTab, false);
      this.activeTab.setAttribute('tabindex', '-1');
      this.activeTab = null;
    }

    /**
     * Sets the active state of a tab and associated content panel.
     * @param {Element} tab - Tab element (button).
     * @param {boolean} active - Set the tab as active.
     */
    static setTabState(tab, active) {
      tab.setAttribute('aria-selected', active);

      const panelId = tab.getAttribute('aria-controls');
      const panel = document.getElementById(panelId);
      if (panel) panel.hidden = !active;
    }
  }

  customElements.define('tabbed-content', Tabs);

  // Allow links with [data-switch-tab="tab-X"] to switch tabs from anywhere on the page
  document.addEventListener('click', (evt) => {
    const reviewTrigger = evt.target.closest('[data-scroll-to-reviews]');
    if (reviewTrigger) {
      const selector = reviewTrigger.getAttribute('href') ||
        reviewTrigger.getAttribute('data-scroll-target') ||
        '#all-reviews';
      if (smoothScrollToTarget(selector)) {
        evt.preventDefault();
      }
      return;
    }

    const trigger = evt.target.closest('[data-switch-tab]');
    if (!trigger) return;

    const tabId = trigger.getAttribute('data-switch-tab');
    const currentTabs = trigger.closest('tabbed-content');
    const tab = currentTabs
      ? currentTabs.querySelector(`#${CSS.escape(tabId)}[role="tab"]`)
      : document.getElementById(tabId);
    const tabsEl = tab ? tab.closest('tabbed-content') : null;

    if (!tab || !tab.matches('[role="tab"]') || !tabsEl || typeof tabsEl.activateTab !== 'function') return;

    evt.preventDefault();
    if (tab !== tabsEl.activeTab) {
      tabsEl.activateTab(tab);
    }
    tabsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

}
